import { faList, faSquareKanban } from '@fortawesome/pro-solid-svg-icons'
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import clsx from 'clsx'
import { usePostHog } from 'posthog-js/react'
import { type ReactElement, useCallback, useContext } from 'react'
import { z } from 'zod'
import { FilterBar } from '~/components/Filters/FilterBar/FilterBar'
import type { FiltersState } from '~/components/Filters/FilterBar/types'
import { resolveFilters } from '~/components/Filters/FilterBar/utils'
import { Paper } from '~/components/Global/Blocks/Paper/Paper'
import { Loader } from '~/components/Global/Elements/Loader/Loader'
import { SegmentedControl } from '~/components/Global/Interactions/SegmentedControl/SegmentedControl'
import { QuickSortControl } from '~/components/Sort/QuickSortControl'
import { useUpdateConfig } from '~/domains/configuration/mutations/updateConfig'
import { useConfig } from '~/domains/configuration/queries/config'
import { captureOrderAction } from '~/domains/orders/analytics'
import { GlobalKanban } from '~/domains/orders/components/GlobalKanban'
import { OrdersList } from '~/domains/orders/components/OrdersList'
import {
  contentWrapper,
  contentWrapperWide
} from '~/pages/styles/Dashboard.css'
import { listWrapper, quickControlsBar } from '~/pages/styles/Orders.css'
import { AuthContext } from '~/utils/contextes'
import useDocumentTitle from '~/utils/documentTitle'
import { Header } from './-components/Header'
import { useOrderFilterDefinitions } from './-components/orderFilterDefinitions'

const searchSchema = z
  .object({
    sort: z.string().default('created'),
    sortDirection: z.enum(['asc', 'desc']).default('desc'),
    view: z.enum(['list', 'kanban']).default('list')
  })
  .catchall(z.string())

const Orders = (): ReactElement => {
  const posthog = usePostHog()
  const currentUser = useContext(AuthContext)
  const navigate = useNavigate({ from: '/orders/' })
  const { data: orderCurrentUserDefaultFilter } = useConfig(
    'listing.orders_current_user_filter'
  )

  const { sort, sortDirection, view, ...panelFilters } = useSearch({
    from: '/_dashboard/orders/'
  })

  const { mutate: updateKanbanOnboarding } = useUpdateConfig(
    'onboarding.kanban_onboarding'
  )

  const filterDefinitions = useOrderFilterDefinitions()

  useDocumentTitle('Commandes')

  const setFilters = useCallback(
    (filters: FiltersState, areDefault = false) => {
      const clean = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== '')
      )
      void navigate({
        search: { sort, sortDirection, view, ...clean },
        replace: areDefault
      })
    },
    [navigate, sort, sortDirection, view]
  )

  const setSortAndDirection = ({
    newSort = sort,
    newSortDirection = sortDirection
  }: {
    newSort?: string
    newSortDirection?: 'asc' | 'desc'
  }) => {
    void navigate({
      search: {
        sort: newSort,
        sortDirection: newSortDirection,
        view,
        ...panelFilters
      }
    })
  }

  const setView = (newView: 'list' | 'kanban') => {
    void navigate({
      search: { sort, sortDirection, view: newView, ...panelFilters }
    })
  }

  if (orderCurrentUserDefaultFilter === undefined) {
    return <Loader />
  }

  const defaultFilters: FiltersState = { status: '1' }
  if (orderCurrentUserDefaultFilter && currentUser) {
    defaultFilters.user = String(currentUser.id)
  }

  const orderFilters = resolveFilters(
    { sort, sortDirection, ...panelFilters },
    filterDefinitions
  )

  return (
    <div className={clsx(contentWrapper, contentWrapperWide)}>
      <Header />
      <Paper className={listWrapper}>
        <div className={quickControlsBar}>
          <FilterBar
            definitions={filterDefinitions}
            filters={panelFilters}
            onChange={setFilters}
            savedSearchesKey="search.saved_orders"
            defaultFilters={defaultFilters}
            analyticsKey="orders"
          />
          {view === 'list' && (
            <QuickSortControl
              asMenu={true}
              sort={sort}
              sortDirection={sortDirection}
              options={[
                { label: 'Urgence', value: 'urgency' },
                { label: 'Statut', value: 'status' },
                { label: 'Date de création', value: 'created' },
                { label: 'Entreprise (raison sociale)', value: 'company' }
              ]}
              onChange={({
                sort: nextSort,
                sortDirection: nextSortDirection
              }) =>
                setSortAndDirection({
                  newSort: nextSort,
                  newSortDirection: nextSortDirection
                })
              }
            />
          )}
          <SegmentedControl
            buttonSize="small"
            options={[
              { value: 'list', icon: faList, tooltip: 'Vue liste' },
              {
                value: 'kanban',
                icon: faSquareKanban,
                tooltip: 'Vue kanban globale'
              }
            ]}
            value={view}
            onChange={(v) => {
              if (v === 'kanban') {
                captureOrderAction(posthog, 'view_kanban')
              }
              setView(v as 'list' | 'kanban')
              if (v === 'kanban') {
                updateKanbanOnboarding({ value: true })
              }
            }}
          />
        </div>
        {view === 'list' && (
          <OrdersList
            filters={orderFilters}
            onResetFilters={() => setFilters({})}
          />
        )}
        {view === 'kanban' && <GlobalKanban orderFilters={orderFilters} />}
      </Paper>
    </div>
  )
}

export const Route = createFileRoute('/_dashboard/orders/')({
  validateSearch: searchSchema,
  component: Orders
})
